import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Calendar, Brain, Timer, Sparkles, ArrowRight, Clock, Target, Zap, MessageSquare, ClipboardList, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <CheckCircle2 className="w-6 h-6" />,
      title: "Smart Task Management",
      description: "Create, organize, and complete tasks with ease. Support for subtasks, priorities, and labels."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Visual Calendar",
      description: "Plan your day, week, or month with our beautiful calendar. Drag and drop tasks effortlessly."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Planning",
      description: "Let AI suggest optimal schedules, detect patterns, and help you stay productive."
    },
    {
      icon: <Timer className="w-6 h-6" />,
      title: "Focus Timer",
      description: "Pomodoro-style focus sessions to boost your concentration and track progress."
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Team Chat",
      description: "Collaborate with your team in real-time. Share updates, discuss tasks, and stay connected."
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      title: "Forms & Feedback",
      description: "Create custom forms, collect responses, and gather feedback from your team effortlessly."
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Launchpad",
      description: "Quick access to your favorite apps and links. Organize and launch productivity tools instantly."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Natural Language Input",
      description: "Just type 'Meeting tomorrow 3pm' and watch the magic happen."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Recurring Tasks",
      description: "Set daily, weekly, or custom recurring tasks. Never miss a routine again."
    }
  ];

  const stats = [
    { value: "10x", label: "Productivity Boost" },
    { value: "2M+", label: "Tasks Completed" },
    { value: "98%", label: "User Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-xl text-gray-900 leading-tight" style={{ fontFamily: 'Manrope' }}>CORE</span>
                <span className="text-[9px] font-semibold text-gray-600 leading-tight tracking-wider hidden sm:block">Create • Organize • Review • Execute</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full px-6"
                  data-testid="go-to-dashboard-btn"
                >
                  Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-gray-600 hover:text-gray-900"
                    data-testid="login-btn"
                  >
                    Log in
                  </Button>
                  <Button 
                    onClick={() => navigate('/signup')}
                    className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full px-6"
                    data-testid="get-started-btn"
                  >
                    Get Started <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 gradient-hero">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slideUp">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0F2F1] rounded-full text-[#0F4C5C] text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Productivity
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: 'Manrope' }}>
                Everything that matters.<br />
                <span className="text-[#4A9CC7]">In one CORE.</span>
              </h1>
              <p className="text-sm font-bold text-gray-700 mb-4 tracking-widest">CREATE • ORGANIZE • REVIEW • EXECUTE</p>
              <p className="text-lg text-gray-600 mb-8 max-w-lg">
                Your AI-powered productivity companion. Manage tasks, plan your day, and achieve more with smart automation and beautiful design.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')}
                  className="bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-full px-8 py-6 text-lg"
                  data-testid="hero-cta-btn"
                >
                  Let’s Begin <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full px-8 py-6 text-lg border-gray-300"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative animate-fadeIn">
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#4A9CC7] flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Today's Plan</p>
                    <p className="text-sm text-gray-500">3 tasks pending</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {['Complete project report', 'Team standup at 10am', 'Review design mockups'].map((task, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className={`w-5 h-5 rounded-full border-2 ${i === 0 ? 'bg-[#4A9CC7] border-[#4A9CC7]' : 'border-gray-300'} flex items-center justify-center`}>
                        {i === 0 && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`${i === 0 ? 'line-through text-gray-400' : 'text-gray-700'}`}>{task}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-gradient-to-r from-[#F0F9FF] to-[#E0F2F1] rounded-xl">
                  <div className="flex items-center gap-2 text-[#0F4C5C]">
                    <Brain className="w-5 h-5" />
                    <span className="font-medium">AI Suggestion</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Start with the project report - it&apos;s your highest priority! 💪</p>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#4A9CC7]/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#E0F2F1] rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl font-bold text-[#4A9CC7]" style={{ fontFamily: 'Manrope' }}>{stat.value}</p>
                <p className="text-gray-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Everything You Need to <span className="text-[#4A9CC7]">Stay Productive</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful features designed for professionals, students, and everyone who wants to achieve more.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="feature-card group"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#F0F9FF] text-[#4A9CC7] flex items-center justify-center mb-4 group-hover:bg-[#4A9CC7] group-hover:text-white transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-r from-[#4A9CC7] to-[#0F4C5C]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to Boost Your Productivity?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Join thousands of users who have transformed their daily routines with CORE.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')}
            className="bg-white text-[#4A9CC7] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-semibold"
            data-testid="cta-btn"
          >
            Start Free Today <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white" style={{ fontFamily: 'Manrope' }}>CORE</span>
            </div>
            <p className="text-gray-400 text-sm">
              Support & Enquiries: <a href="mailto:hello@coreone.co.in" className="text-[#4A9CC7] hover:underline">hello@coreone.co.in</a>
            </p>
            <p className="text-gray-400 text-sm">
              © 2025 CORE. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
